import type { ErrorComponentProps } from '@tanstack/react-router'
import { useRouter } from '@tanstack/react-router'
import { IconHouse4Outline18, IconTriangleWarningOutline18 } from 'nucleo-ui-outline-18'
import { useEffect } from 'react'

import { MarketingLink } from '~/components/marketing/marketing-link'
import { Button, buttonVariants } from '~/components/ui/button'
import { reportError } from '~/lib/sentry-lazy'
import { cn } from '~/lib/utils'

/**
 * Router-wide error boundary, wired as `defaultErrorComponent` in
 * `src/router.tsx`. TanStack Router catches route render/loader errors in its
 * own boundary, so they never reach Sentry's global handlers — this component
 * is the only place they get reported from.
 *
 * Strings are hardcoded pt-BR (not Lingui): this can render on the
 * provider-free marketing branch of `RootDocument`, where there is no
 * `I18nProvider`.
 */
export const RouteErrorComponent = ({ error, reset }: ErrorComponentProps) => {
  const router = useRouter()

  useEffect(() => {
    reportError(error, { tags: { boundary: 'route' } })
  }, [error])

  return (
    <main
      className="flex min-h-svh flex-col items-center justify-center gap-4 bg-canvas px-6 text-center"
      // The e2e smoke spec asserts the absence of this node on every critical
      // route. It keys on the testid rather than the copy above because a
      // route crash must stay detectable when the wording changes — and
      // because a boundary-caught error never reaches `window.onerror`, so
      // this element is the only signal a browser test can see.
      data-testid="route-error"
    >
      <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <IconTriangleWarningOutline18 className="size-6" />
      </div>
      <div className="space-y-1">
        <p className="font-semibold text-2xl tracking-tight">Algo deu errado</p>
        <p className="text-muted-foreground text-sm">
          Ocorreu um erro inesperado. Tente novamente ou volte para o início.
        </p>
      </div>
      {import.meta.env.DEV && error instanceof Error ? (
        <pre className="max-h-48 max-w-full overflow-auto rounded-md bg-muted px-4 py-3 text-left text-muted-foreground text-xs">
          {error.stack ?? error.message}
        </pre>
      ) : null}
      <div className="mt-1 flex items-center gap-2">
        <Button
          onClick={() => {
            reset()
            void router.invalidate()
          }}
        >
          Tentar novamente
        </Button>
        <MarketingLink className={cn(buttonVariants({ variant: 'outline' }))} href="/">
          <IconHouse4Outline18 className="size-4" />
          Voltar para o início
        </MarketingLink>
      </div>
    </main>
  )
}
